import {
	BrowserRouter,
	Routes,
	Route,
	Navigate,
	useNavigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { UnsavedChangesProvider } from "./contexts/UnsavedChangesContext";
import { FontSizeProvider } from "./contexts/FontSizeContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationCountsProvider } from "./contexts/NotificationCountsContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Layout } from "./components/Layout";
import { CookieBanner } from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";
import { useAppUpdateCheck } from "./lib/appVersion";
import Landing from "./pages/Landing";
import Info from "./pages/Info";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Athletes from "./pages/Athletes";
import Exercises from "./pages/Exercises";
import Programs from "./pages/Programs";
import CompetitionHub from "./pages/CompetitionHub";
import ProgramBuilder from "./pages/ProgramBuilder";
import ProgramView from "./pages/ProgramView";
import CoachTeams from "./pages/CoachTeams";
import SportConfig from "./pages/SportConfig";
import SportModelEditor from "./pages/SportModelEditor";
import McpAuthorize from "./pages/McpAuthorize";
import AISettings from "./pages/AISettings";
import AIGuide from "./pages/AIGuide";
import Workouts from "./pages/workouts/Workouts";
import Profile from "./pages/Profile";
import WellnessDiary from "./pages/WellnessDiary";
import PerformanceProfile from "./pages/PerformanceProfile";
import ShootingLeaderboards from "./pages/ShootingLeaderboards";
import ShootingDrills from "./pages/ShootingDrills";
import ShootingAdmin from "./pages/ShootingAdmin";
import CoachLibrary from "./pages/CoachLibrary";
import ConditioningLibrary from "./pages/ConditioningLibrary";
import Settings from "./pages/Settings";
import AdminDashboard from "./pages/AdminDashboard";
import AITrainingDocs from "./pages/AITrainingDocs";
import { AI_TRAINING_DOCS_VISIBLE } from "./lib/featureFlags";
import Messages from "./pages/Messages";
import CoachFeedback from "./pages/CoachFeedback";
import Monitoring from "./pages/Monitoring";
import AthleteDetail from "./pages/AthleteDetail";
import Billing from "./pages/Billing";
import MyPayments from "./pages/MyPayments";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import InviteOnboarding from "./pages/InviteOnboarding";
/* Catalogo del design system per la verifica del tema. La rotta esiste solo
   fuori dalla build di produzione: vedi piu' sotto. */
import ThemeGallery from "./pages/ThemeGallery";
import "./App.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID =
	"444440687616-gosrgthajj627uhvp67hqs88qg90degs.apps.googleusercontent.com";

function AppRouter() {
	return (
		<Routes>
			<Route path="/login" element={<Login />} />
			<Route path="/reset-password" element={<ResetPassword />} />
			<Route path="/verify-email" element={<VerifyEmail />} />
			<Route path="/invite" element={<InviteOnboarding />} />
			{process.env.NODE_ENV !== "production" && (
				<Route path="/dev/theme" element={<ThemeGallery />} />
			)}
			<Route
				path="/dashboard"
				element={
					<ProtectedRoute>
						<Layout>
							<Dashboard />
						</Layout>
					</ProtectedRoute>
				}
			/>
			<Route
				path="/athletes"
				element={
					<ProtectedRoute>
						<Athletes />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/athletes/:athleteId"
				element={
					<ProtectedRoute>
						<AthleteDetail />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/feedback"
				element={
					<ProtectedRoute>
						<CoachFeedback />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/monitoring/:athleteId?"
				element={
					<ProtectedRoute>
						<Monitoring />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/competition-hub"
				element={
					<ProtectedRoute>
						<CompetitionHub />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/exercises"
				element={
					<ProtectedRoute>
						<Exercises />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/exercises/new"
				element={
					<ProtectedRoute>
						<Exercises />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/coach-teams"
				element={
					<ProtectedRoute>
						<CoachTeams />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/sport-config"
				element={
					<ProtectedRoute>
						<SportConfig />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/sport-config/models/:modelId"
				element={
					<ProtectedRoute>
						<SportModelEditor />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/programs"
				element={
					<ProtectedRoute>
						<Programs />
					</ProtectedRoute>
				}
			/>
			{/* Consenso del connettore MCP. Non protetta da ProtectedRoute: il
			    redirect arriva da un client esterno e la pagina gestisce da se'
			    il caso "non autenticato", parcheggiando la destinazione. */}
			<Route path="/mcp/authorize" element={<McpAuthorize />} />
			<Route
				path="/programs/new"
				element={
					<ProtectedRoute>
						<ProgramBuilder />
					</ProtectedRoute>
				}
			/>
			{/* Sola lettura di un programma condiviso: dichiarata PRIMA della
			    rotta dinamica, o "/programs/:programId" la intercetterebbe.
			    Vedi docs/coach-teams/DECISIONS.md §2. */}
			<Route
				path="/programs/:programId/view"
				element={
					<ProtectedRoute>
						<ProgramView />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/programs/:programId"
				element={
					<ProtectedRoute>
						<ProgramBuilder />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/ai-assistant"
				element={
					<ProtectedRoute>
						<AISettings />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/ai-guide"
				element={
					<ProtectedRoute>
						<AIGuide />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/workouts"
				element={
					<ProtectedRoute>
						<Workouts />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/library"
				element={
					<ProtectedRoute>
						<CoachLibrary />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/conditioning-library"
				element={
					<ProtectedRoute>
						<ConditioningLibrary />
					</ProtectedRoute>
				}
			/>

			<Route
				path="/profile"
				element={
					<ProtectedRoute>
						<Profile />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/performance"
				element={
					<ProtectedRoute>
						<PerformanceProfile />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/shooting-admin"
				element={
					<ProtectedRoute>
						<ShootingAdmin />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/shooting-drills"
				element={
					<ProtectedRoute>
						<ShootingDrills />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/shooting-leaderboards"
				element={
					<ProtectedRoute>
						<ShootingLeaderboards />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/settings"
				element={
					<ProtectedRoute>
						<Settings />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/admin"
				element={
					<ProtectedRoute>
						<AdminDashboard />
					</ProtectedRoute>
				}
			/>
			{/* Documenti formativi: la pagina resta montata solo se il flag e'
			    acceso (frontend/src/lib/featureFlags.js). Spento, l'indirizzo
			    porta alla pagina dell'assistente invece che a una schermata
			    raggiungibile solo da chi si ricorda l'URL. */}
			<Route
				path="/ai-training-docs"
				element={
					AI_TRAINING_DOCS_VISIBLE ? (
						<ProtectedRoute>
							<AITrainingDocs />
						</ProtectedRoute>
					) : (
						<Navigate to="/ai-assistant" replace />
					)
				}
			/>
			<Route
				path="/messages"
				element={
					<ProtectedRoute>
						<Messages />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/billing"
				element={
					<ProtectedRoute>
						<Billing />
					</ProtectedRoute>
				}
			/>
			<Route
				path="/my-payments"
				element={
					<ProtectedRoute>
						<MyPayments />
					</ProtectedRoute>
				}
			/>
			<Route path="/privacy-policy" element={<PrivacyPolicy />} />
			<Route path="/cookie-policy" element={<CookiePolicy />} />
			<Route
				path="/wellness"
				element={
					<ProtectedRoute>
						<WellnessDiary />
					</ProtectedRoute>
				}
			/>
			<Route path="/" element={<Landing />} />
			<Route path="/info" element={<Info />} />
			<Route path="*" element={<Navigate to="/dashboard" replace />} />
		</Routes>
	);
}

/* L'app aperta si accorge dei rilasci nuovi (lib/appVersion.js). Vive qui,
   dentro Router e provider ma fuori dalle pagine: una volta sola per tutta la
   sessione, non rimontata a ogni rotta col Layout. */
function AppUpdateWatcher() {
	useAppUpdateCheck();
	return null;
}

function PushNotificationBridge() {
	const navigate = useNavigate();

	useEffect(() => {
		const handleServiceWorkerMessage = (event) => {
			if (
				event?.data?.type !== "OPEN_THREAD" &&
				event?.data?.type !== "OPEN_NOTIFICATION_URL"
			) {
				return;
			}
			if (event.data.type === "OPEN_THREAD" && event?.data?.threadId) {
				navigate("/messages", {
					state: { openThreadId: event.data.threadId },
				});
				return;
			}

			const targetUrl = String(event?.data?.url || "");
			if (!targetUrl) return;
			try {
				const parsedUrl = new URL(targetUrl, window.location.origin);
				if (parsedUrl.origin !== window.location.origin) return;
				if (parsedUrl.pathname === "/messages" && event?.data?.threadId) {
					navigate("/messages", {
						state: { openThreadId: event.data.threadId },
					});
					return;
				}
				navigate(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
			} catch (err) {
				return;
			}
		};

		navigator.serviceWorker?.addEventListener(
			"message",
			handleServiceWorkerMessage,
		);
		return () => {
			navigator.serviceWorker?.removeEventListener(
				"message",
				handleServiceWorkerMessage,
			);
		};
	}, [navigate]);

	return null;
}

function App() {
	useEffect(() => {
		const standaloneMediaQuery = window.matchMedia(
			"(display-mode: standalone)",
		);
		const fullscreenMediaQuery = window.matchMedia(
			"(display-mode: fullscreen)",
		);
		const minimalUiMediaQuery = window.matchMedia("(display-mode: minimal-ui)");
		const parsePxValue = (value) => {
			const parsed = Number.parseFloat(value || "0");
			return Number.isFinite(parsed) ? parsed : 0;
		};

		const updateRuntimeSafeArea = (isStandalone) => {
			const root = document.documentElement;
			const styles = window.getComputedStyle(root);
			const envTop = parsePxValue(styles.getPropertyValue("--safe-area-top"));
			const envBottom = parsePxValue(
				styles.getPropertyValue("--safe-area-bottom"),
			);
			const viewport = window.visualViewport;
			const runtimeTop = viewport ? Math.max(0, viewport.offsetTop || 0) : 0;
			const runtimeBottom = viewport
				? Math.max(
						0,
						window.innerHeight -
							((viewport.offsetTop || 0) +
								(viewport.height || window.innerHeight)),
					)
				: 0;
			const resolvedTop = isStandalone ? envTop : Math.max(envTop, runtimeTop);
			const resolvedBottom = isStandalone
				? envBottom
				: Math.max(envBottom, runtimeBottom);

			root.style.setProperty("--safe-area-top-dynamic", `${resolvedTop}px`);
			root.style.setProperty(
				"--safe-area-bottom-dynamic",
				`${resolvedBottom}px`,
			);
		};

		const updateStandaloneClass = () => {
			const isStandalone =
				window.navigator.standalone === true ||
				standaloneMediaQuery.matches ||
				fullscreenMediaQuery.matches ||
				minimalUiMediaQuery.matches ||
				document.referrer.startsWith("android-app://");
			document.documentElement.classList.toggle(
				"standalone-mode",
				isStandalone,
			);
			document.body.classList.toggle("standalone-mode", isStandalone);
			updateRuntimeSafeArea(isStandalone);
			window.requestAnimationFrame(() => updateRuntimeSafeArea(isStandalone));
		};

		updateStandaloneClass();
		if (
			standaloneMediaQuery.addEventListener &&
			fullscreenMediaQuery.addEventListener &&
			minimalUiMediaQuery.addEventListener
		) {
			standaloneMediaQuery.addEventListener("change", updateStandaloneClass);
			fullscreenMediaQuery.addEventListener("change", updateStandaloneClass);
			minimalUiMediaQuery.addEventListener("change", updateStandaloneClass);
		} else if (standaloneMediaQuery.addListener) {
			standaloneMediaQuery.addListener(updateStandaloneClass);
			fullscreenMediaQuery.addListener(updateStandaloneClass);
			minimalUiMediaQuery.addListener(updateStandaloneClass);
		}
		window.addEventListener("pageshow", updateStandaloneClass);
		window.addEventListener("resize", updateStandaloneClass);
		window.addEventListener("orientationchange", updateStandaloneClass);
		document.addEventListener("visibilitychange", updateStandaloneClass);
		window.visualViewport?.addEventListener("resize", updateStandaloneClass);
		window.visualViewport?.addEventListener("scroll", updateStandaloneClass);

		return () => {
			if (
				standaloneMediaQuery.removeEventListener &&
				fullscreenMediaQuery.removeEventListener &&
				minimalUiMediaQuery.removeEventListener
			) {
				standaloneMediaQuery.removeEventListener(
					"change",
					updateStandaloneClass,
				);
				fullscreenMediaQuery.removeEventListener(
					"change",
					updateStandaloneClass,
				);
				minimalUiMediaQuery.removeEventListener(
					"change",
					updateStandaloneClass,
				);
			} else if (standaloneMediaQuery.removeListener) {
				standaloneMediaQuery.removeListener(updateStandaloneClass);
				fullscreenMediaQuery.removeListener(updateStandaloneClass);
				minimalUiMediaQuery.removeListener(updateStandaloneClass);
			}
			window.removeEventListener("pageshow", updateStandaloneClass);
			window.removeEventListener("resize", updateStandaloneClass);
			window.removeEventListener("orientationchange", updateStandaloneClass);
			document.removeEventListener("visibilitychange", updateStandaloneClass);
			window.visualViewport?.removeEventListener(
				"resize",
				updateStandaloneClass,
			);
			window.visualViewport?.removeEventListener(
				"scroll",
				updateStandaloneClass,
			);
		};
	}, []);

	return (
		<div className="App">
			<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
				<BrowserRouter>
					<LanguageProvider>
						<UnsavedChangesProvider>
							<FontSizeProvider>
								<ThemeProvider>
									<AuthProvider>
										<NotificationCountsProvider>
											<PushNotificationBridge />
											<AppUpdateWatcher />
											<AppRouter />
											<CookieBanner />
											<Toaster />
										</NotificationCountsProvider>
									</AuthProvider>
								</ThemeProvider>
							</FontSizeProvider>
						</UnsavedChangesProvider>
					</LanguageProvider>
				</BrowserRouter>
			</GoogleOAuthProvider>
			<div className="noise-overlay" />
		</div>
	);
}

export default App;
